import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/cn';

/**
 * ProgressBar variant styles
 *
 * Available variants:
 * - `default` - Blue progress (default)
 * - `success` - Green progress for successful states
 * - `warning` - Amber/yellow progress for warnings
 * - `error` - Red progress for error states
 *
 * Available sizes:
 * - `sm` - Small (h-2)
 * - `md` - Medium (h-3, default)
 * - `lg` - Large (h-4)
 */
const progressBarVariants = cva('relative w-full overflow-hidden rounded-full bg-mv-gray-200', {
  variants: {
    variant: {
      default: '[&>div]:bg-mv-blue-600',
      success: '[&>div]:bg-mv-green-700',
      warning: '[&>div]:bg-mv-amber-500',
      error: '[&>div]:bg-mv-error-500',
    },
    size: {
      sm: 'h-2',
      md: 'h-3',
      lg: 'h-4',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

export interface ProgressBarProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof progressBarVariants> {
  /**
   * Current progress value (0-100)
   */
  value: number;
  /**
   * Maximum value (defaults to 100)
   */
  max?: number;
  /**
   * Optional label text to display above the progress bar
   */
  label?: string;
  /**
   * Whether to show the percentage value
   */
  showPercentage?: boolean;
  /**
   * Whether to show the label (either custom label or percentage)
   */
  showLabel?: boolean;
  /**
   * Whether to animate the progress fill
   */
  animated?: boolean;
}

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      className,
      variant,
      size,
      value,
      max = 100,
      label,
      showPercentage = false,
      showLabel = false,
      animated = true,
      ...props
    },
    ref
  ) => {
    // Clamp value between 0 and max
    const clampedValue = Math.min(Math.max(0, value), max);
    const percentage = (clampedValue / max) * 100;

    // Determine what to display as label
    const displayLabel = label || (showPercentage ? `${Math.round(percentage)}%` : null);

    return (
      <div ref={ref} className="w-full space-y-1.5" {...props}>
        {(showLabel || label) && displayLabel && (
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-mv-gray-700">{displayLabel}</span>
            {label && showPercentage && (
              <span className="text-mv-gray-500">{Math.round(percentage)}%</span>
            )}
          </div>
        )}
        <div className={cn(progressBarVariants({ variant, size, className }))}>
          <div
            className={cn('h-full rounded-full transition-all', animated && 'duration-500 ease-out')}
            style={{ width: `${percentage}%` }}
            role="progressbar"
            aria-valuenow={clampedValue}
            aria-valuemin={0}
            aria-valuemax={max}
            aria-label={displayLabel || `${Math.round(percentage)}% complete`}
          />
        </div>
      </div>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

export { progressBarVariants };
